import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListTodo, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTaskList } from "../hooks/useTasks";
import { NewTaskDrawer } from "./NewTaskDrawer";

const BORDER = "hsl(210,30%,85%)";

type Props = {
  meetingId?: string;
  decisionId?: string;
  ideaId?: string;
  milestoneId?: string;
  /** Pre-fill values forwarded to the New Task drawer so the new task
   *  is created already linked to the surrounding entity. */
  prefill?: {
    companyId?: string | null;
    productId?: string | null;
    projectId?: string | null;
    meetingId?: string | null;
    meetingTitle?: string | null;
    decisionId?: string | null;
    decisionTitle?: string | null;
    ideaId?: string | null;
    ideaTitle?: string | null;
    milestoneId?: string | null;
    milestoneTitle?: string | null;
    locked?: {
      meetingId?: boolean;
      decisionId?: boolean;
      ideaId?: boolean;
      milestoneId?: boolean;
    };
  };
};

export function TasksTabContent({
  meetingId,
  decisionId,
  ideaId,
  milestoneId,
  prefill,
}: Props) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data, isLoading } = useTaskList({
    meetingId,
    decisionId,
    ideaId,
    milestoneId,
    limit: 100,
  });
  const tasks = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
        </div>
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          New Task
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : tasks.length === 0 ? (
        <div
          className="rounded-xl border bg-white py-12 text-center"
          style={{ borderColor: BORDER }}
        >
          <ListTodo
            className="mx-auto h-8 w-8 text-muted-foreground"
            strokeWidth={1.5}
          />
          <div className="mt-2 text-sm text-muted-foreground">
            No tasks yet
          </div>
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <table className="w-full text-[13px]">
            <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                  #
                </th>
                <th className="px-[14px] py-[9px]">Title</th>
                <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                  Status
                </th>
                <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                  Priority
                </th>
                <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
                  Assignee
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {tasks.map((t) => (
                <tr
                  key={t.id}
                  className="cursor-pointer hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/tasks/${t.id}`)}
                >
                  <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                    {t.code}
                  </td>
                  <td className="px-[14px] py-[11px] font-medium text-foreground">
                    {t.title}
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {t.status}
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {t.priority}
                  </td>
                  <td className="px-[14px] py-[11px] text-muted-foreground">
                    {t.assignedToName ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewTaskDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        prefill={prefill}
      />
    </div>
  );
}
